import { getAllFromDB, saveImageToDB } from "./indexedDB";
import { store } from "../store/store";
import { addToCart, clearCart } from "../store/cartSlice";

export const captureCartForRecovery = async (email, affiliateCode, isPartnerAffiliate) => {
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || localStorage.getItem("cart_id")) return;
  const cartItems = store.getState().cart.cartItems;
  if (!cartItems || cartItems.length === 0) return;

  const cartId = crypto.randomUUID();
  localStorage.setItem("cart_id", cartId);

  const onProductImagesAll = await getAllFromDB("onProductImages");
  const resultImagesAll = await getAllFromDB("resultImages");
  const uploadedImagesAll = await getAllFromDB("uploadedImages");

  const products = await Promise.all(
    cartItems.map(async (item) => {
      const resultImageEntry = resultImagesAll.find(img => img.id === item.imageId);
      const resultImageBlob = resultImageEntry?.imageBlob;

      const onProductImageEntry = onProductImagesAll.find(img => img.resultImageId === item.imageId && img.productId === item.id);
      const onProductImageBlob = onProductImageEntry?.imageBlob;

      const uploadedImages = Array.isArray(item.uploadedImageIds)
        ? uploadedImagesAll.filter(img => item.uploadedImageIds.includes(img.id))
        : [];

      return {
        id: item.id,
        labelTemplate: item.labelTemplate || null,
        petType: item.petType || null,
        petName: item.petName || null,
        title: item.name,
        quantity: item.quantity,
        subscription: item.subscription || false,
        imageMetadata: item.imageMetadata || null,
        onProductImage: onProductImageBlob
          ? new File([onProductImageBlob], `${onProductImageEntry.id}.png`, { type: "image/png" })
          : null,
        resultImage: resultImageBlob
          ? new File([resultImageBlob], `${resultImageEntry.id}.png`, { type: "image/png" })
          : null,
        uploadedImages: uploadedImages.map(img =>
          new File([img.imageBlob], `${img.id}.png`, { type: "image/png" })
        )
      };
    })
  );

  const payload = {
    cart_id: cartId,
    email,
    products,
    ref_code: affiliateCode,
    ref_type: affiliateCode && isPartnerAffiliate ? 'partner' : affiliateCode && !isPartnerAffiliate ? 'ref' : null 
  };

  const formData = buildRecoveryFormData(payload);

  try {
    const response = await fetch("https://api.treatink.com/functions/v1/cart-recovery", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      console.warn("Cart recovery API failed:", await response.text());
    }
  } catch (err) {
    console.warn("Cart recovery capture failed:", err);
  }
};


const buildRecoveryFormData = (payload) => {
  const formData = new FormData();

  const strippedPayload = {
    ...payload,
    products: payload.products.map((p, i) => {
      const {
        resultImage, onProductImage, uploadedImages,
        ...rest
      } = p;
      return rest;
    })
  };

  formData.append("payload", new Blob([JSON.stringify(strippedPayload)], { type: "application/json" }));

  payload.products.forEach((p, i) => {
    if (p.resultImage instanceof File) {
      formData.append(`products[${i}].resultImage`, p.resultImage);
    }
    if (p.onProductImage instanceof File) {
      formData.append(`products[${i}].onProductImage`, p.onProductImage);
    }
    if (Array.isArray(p.uploadedImages)) {
      p.uploadedImages.forEach((file, j) => {
        if (file instanceof File) {
          formData.append(`products[${i}].uploadedImages[${j}]`, file);
        }
      });
    }
  });

  return formData;
};

export const restoreCartFromRecovery = async (recoveredCart, dispatch, products, cartItems) => {
  const existingCartId = localStorage.getItem("restored_cart_id");

  if (
    !recoveredCart.data || !recoveredCart.data.products_data || 
    (existingCartId && cartItems.length !== 0) || 
    !Array.isArray(recoveredCart.data.products_data)
  ) return;
  
  dispatch(clearCart());
  const parsedProducts = recoveredCart.data.products_data;
  
  if (!Array.isArray(parsedProducts)) {
    console.error("Parsed products_data is not an array");
    return;
  }

  for (const item of parsedProducts) {
    const {
      id,
      title,
      petName,
      petType,
      quantity,
      subscription,
      imageMetadata,
      labelTemplate,
      resultImageUrl,
      onProductImageUrl,
      uploadedImageUrls = [],
    } = item;

    const product = products.find(p => p.ID === id);
    if (!product) continue;

    const imageId = Date.now().toString();
    const uploadedImageIds = [];

    if (onProductImageUrl) {
      try {
        const blob = await fetch(onProductImageUrl).then(r => r.blob());
        await saveImageToDB("onProductImages", imageId, blob, product.id, imageId);
      } catch (err) {
        console.warn("Failed to restore onProductImage:", err);
      }
    }

    for (const url of uploadedImageUrls) {
      try {
        const id = crypto.randomUUID();
        const blob = await fetch(url).then(r => r.blob());
        await saveImageToDB("uploadedImages", id, blob);
        uploadedImageIds.push(id);
      } catch (err) {
        console.warn("Failed to restore uploadedImage:", err);
      }
    }

    if (resultImageUrl) {
      try {
        const blob = await fetch(resultImageUrl).then(r => r.blob());
        await saveImageToDB("resultImages", imageId, blob, product.id, imageId, uploadedImageIds);
      } catch (err) {
        console.warn("Failed to restore resultImage:", err);
      }
    }

    dispatch(addToCart({
      ID: product.id,
      Title: title,
      SKU: product.sku,
      Flavor: product.flavor,
      Price: parseFloat(product.price),
      subscription,
      imageId,
      quantity,
      uploadedImageIds,
      labelTemplate,
      imageMetadata:  typeof imageMetadata === "string"
        ? JSON.parse(imageMetadata)
        : imageMetadata,
      petName: petName,
      petType: petType
    }));
    localStorage.setItem("cart_id", recoveredCart.data.cart_id);
    localStorage.setItem("restored_cart_id", recoveredCart.data.cart_id);
  }
};